/**
 * useColdOptimization Hook
 *
 * Manages cold storage facility optimization submission and results.
 * Supports full-year (12-month) optimization with progress tracking.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@vortx/shared-ui';
import { optimizeFacility } from '../services/coldStorageService';

// TMY hour boundaries for each month (same as weather.py)
export const MONTH_OPTIONS = [
    { value: 'jan', label: 'January', start: 0, hours: 744 },
    { value: 'feb', label: 'February', start: 744, hours: 672 },
    { value: 'mar', label: 'March', start: 1416, hours: 744 },
    { value: 'apr', label: 'April', start: 2160, hours: 720 },
    { value: 'may', label: 'May', start: 2880, hours: 744 },
    { value: 'jun', label: 'June', start: 3624, hours: 720 },
    { value: 'jul', label: 'July', start: 4344, hours: 744 },
    { value: 'aug', label: 'August', start: 5088, hours: 744 },
    { value: 'sep', label: 'September', start: 5832, hours: 720 },
    { value: 'oct', label: 'October', start: 6552, hours: 744 },
    { value: 'nov', label: 'November', start: 7296, hours: 720 },
    { value: 'dec', label: 'December', start: 8016, hours: 744 },
];

/**
 * Compute annual summary from 12 monthly solver results
 */
function computeAnnualSummary(monthResults) {
    let totalEnergyCost = 0;
    let totalDemandCost = 0;
    let peakDemandKw = 0;
    let totalBaselineEnergy = 0;
    let totalBaselineDemand = 0;
    let totalSavings = 0;
    let tariffUsed = null;
    let successCount = 0;

    for (const month of MONTH_OPTIONS) {
        const r = monthResults[month.value];
        if (!r || r.status !== 'optimal') continue;
        successCount++;

        if (r.energy_cost == null) {
            console.error(`[computeAnnualSummary] Optimal month ${month.value} missing energy_cost — annual total will be understated`, r);
        }
        if (r.demand_cost == null) {
            console.error(`[computeAnnualSummary] Optimal month ${month.value} missing demand_cost — annual total will be understated`, r);
        }
        totalEnergyCost += r.energy_cost ?? 0;
        totalDemandCost += r.demand_cost ?? 0;
        if (r.peak_demand_kw > peakDemandKw) peakDemandKw = r.peak_demand_kw;
        if (!tariffUsed && r.tariff_used) tariffUsed = r.tariff_used;

        if (r.baseline) {
            if (r.baseline.energy_cost == null) console.error(`[computeAnnualSummary] Optimal month ${month.value} baseline missing energy_cost`, r.baseline);
            if (r.baseline.demand_cost == null) console.error(`[computeAnnualSummary] Optimal month ${month.value} baseline missing demand_cost`, r.baseline);
            totalBaselineEnergy += r.baseline.energy_cost ?? 0;
            totalBaselineDemand += r.baseline.demand_cost ?? 0;
        }
        if (r.savings) {
            if (r.savings.total_cost == null) console.error(`[computeAnnualSummary] Optimal month ${month.value} savings missing total_cost`, r.savings);
            totalSavings += r.savings.total_cost ?? 0;
        }
    }

    const totalCost = totalEnergyCost + totalDemandCost;
    const baselineTotalCost = totalBaselineEnergy + totalBaselineDemand;

    return {
        total_cost: totalCost,
        energy_cost: totalEnergyCost,
        demand_cost: totalDemandCost,
        peak_demand_kw: peakDemandKw,
        baseline_total_cost: baselineTotalCost,
        total_savings: totalSavings,
        savings_pct: baselineTotalCost > 0 ? (totalSavings / baselineTotalCost) * 100 : 0,
        tariff_used: tariffUsed,
        months_solved: successCount,
    };
}

export function useColdOptimization() {
    const { currentUser } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(null); // { current, total, month }

    /**
     * Submit full-year optimization (12 monthly solves)
     * @param {Object} projectData - Cold storage project config (zones, etc.)
     * @param {Array} weatherData8760 - Full-year hourly ambient temps (8760)
     * @param {Object} meterOptions - { tariff_id }
     */
    const submitAnnualOptimization = useCallback(async (projectData, weatherData8760, meterOptions = {}) => {
        setError(null);
        setSubmitting(true);
        setProgress({ current: 0, total: 12, month: '' });

        try {
            const { zones, tariff_id, electric_tariff_id } = projectData;
            const resolvedTariffId = meterOptions.tariff_id || tariff_id || electric_tariff_id;

            if (!zones || zones.length === 0) {
                throw new Error('Please add at least one zone');
            }

            if (!weatherData8760 || weatherData8760.length < 8760) {
                throw new Error('Full-year weather data is required (8760 hourly temperatures).');
            }

            if (!resolvedTariffId) {
                throw new Error('Please select a tariff in the Meters & Tariffs tab before running optimization.');
            }

            const monthResults = {};

            for (let i = 0; i < MONTH_OPTIONS.length; i++) {
                const month = MONTH_OPTIONS[i];
                setProgress({ current: i + 1, total: 12, month: month.label });

                const ambientSlice = weatherData8760.slice(month.start, month.start + month.hours);

                // Build facility config with horizon_hours matching this month
                const facilityConfig = {
                    facility_type: zones[0]?.type || 'cold_storage_warehouse',
                    zones: zones.map(zone => ({
                        zone_id: zone.id,
                        zone_type: zone.type,
                        temp_min: zone.temp_min,
                        temp_max: zone.temp_max,
                        temp_target: zone.target_temp,
                        temp_initial: zone.temp_min,
                        thermal_resistance_r: zone.thermal_resistance_r,
                        thermal_capacitance_c: zone.thermal_capacitance_c,
                        compressor: {
                            cooling_capacity_kw: zone.compressor?.cooling_capacity_kw || 200,
                            electrical_power_kw: zone.compressor?.electrical_power_kw || 60,
                            cop: zone.compressor?.cop || 3.33,
                            min_runtime_hours: zone.compressor?.min_runtime_hours || 0.25,
                            max_runtime_hours: zone.compressor?.max_runtime_hours || 23,
                            min_off_time_hours: zone.compressor?.min_off_time_hours || 0.25,
                            min_power_fraction: zone.compressor?.min_power_fraction ?? 0.3,
                        },
                    })),
                    horizon_hours: month.hours,
                    timestep_hours: 0.25,
                };

                const options = {
                    tariff_id: resolvedTariffId,
                    start_hour: month.start,
                };

                try {
                    const result = await optimizeFacility(facilityConfig, ambientSlice, options, currentUser);
                    monthResults[month.value] = result;
                } catch (monthErr) {
                    // Record the error for this month but continue with others
                    monthResults[month.value] = {
                        status: 'error',
                        message: monthErr.message || `Failed to optimize ${month.label}`,
                    };
                }
            }

            const annualSummary = computeAnnualSummary(monthResults);
            const annualResults = {
                type: 'annual',
                months: monthResults,
                annual_summary: annualSummary,
            };

            setResults(annualResults);
            setProgress(null);
            return annualResults;
        } catch (err) {
            console.error('Annual optimization failed:', err);
            setError(err.message || 'Optimization failed');
            setProgress(null);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [currentUser]);

    /**
     * Load previously saved results (from Firestore)
     */
    const loadResults = useCallback((savedResults) => {
        if (savedResults && !results) {
            setResults(savedResults);
        }
    }, [results]);

    /**
     * Reset optimization state
     */
    const reset = useCallback(() => {
        setResults(null);
        setError(null);
        setProgress(null);
    }, []);

    return {
        submitting,
        results,
        error,
        progress,
        submitAnnualOptimization,
        loadResults,
        reset,
    };
}
